import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { cardClasses } from '../../styles/tailwindClasses';
import { analyticsActionButtonSx, analyticsPanelSx } from '../../styles/analyticsMsiStyles';
import RuleLimitInput from './RuleLimitInput';
import {
    MANUAL_OBJECTIVES, MAX_SHARPE_HELP, MAX_SHARPE_LABEL, MAX_SHARPE_OBJECTIVE,
} from '../../utils/optimizerObjectives';
import { META_OPTIMIZER_ENABLED, GOALS } from '../../utils/portfolioDesign';

// Objectives that new runs cannot choose. A saved run that used one still
// shows its objective, disabled, so the selection is never blank.
const HIDDEN_OBJECTIVE_LABELS = Object.freeze({
    PortfolioDesign: 'Custom objective balance',
    FactorMomentumAI: 'Factor Momentum AI',
});

const SKILL_HELP = 'Your forecasting skill (information coefficient): the correlation you expect '
    + 'between your trade ideas and what the names go on to return. 0.05 is typical; '
    + 'it must be above 0 and at most 1. Skill scales every expected return alike, so it '
    + 'changes the reported expected return and Sharpe ratio, not the positions. Your next '
    + 'Max Sharpe runs start from the skill you last entered.';

export function formatRunTime(isoTimestamp, now = new Date()) {
    if (!isoTimestamp) return null;
    const issued = new Date(isoTimestamp);
    if (Number.isNaN(issued.getTime())) return null;
    const time = issued.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return issued.toDateString() === now.toDateString()
        ? `Today ${time}`
        : `${issued.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

function RunStatus({ status, onDismiss }) {
    const notice = status?.severity === 'error' || status?.severity === 'warning';
    const [open, setOpen] = useState(false);
    useEffect(() => { setOpen(Boolean(notice)); }, [notice, status?.message]);
    if (!status) return null;
    if (notice) {
        const dismiss = (_event, reason) => {
            if (reason === 'clickaway') return;
            setOpen(false);
            onDismiss?.();
        };
        return <Snackbar key={`${status.severity}:${status.message}`} open={open} autoHideDuration={6000} onClose={dismiss}>
            <Alert severity={status.severity} onClose={dismiss} sx={{ width: '100%' }}>
                {status.message}
            </Alert>
        </Snackbar>;
    }
    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
        >
            {status.severity === 'info' && <CircularProgress size={14} thickness={5} />}
            {status.severity === 'success' && (
                <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} />
            )}
            {status.severity === 'stale' && (
                <WarningAmberRoundedIcon color="warning" sx={{ fontSize: 18 }} />
            )}
            <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                {status.message}
            </Typography>
            {status.detail && (
                <Typography variant="caption" color="text.secondary">
                    {status.detail}
                </Typography>
            )}
        </Box>
    );
}

RunStatus.propTypes = {
    onDismiss: PropTypes.func,
    status: PropTypes.shape({
        severity: PropTypes.oneOf(['error', 'warning', 'success', 'info', 'stale']).isRequired,
        message: PropTypes.string.isRequired,
        detail: PropTypes.string,
    }),
};

// Run-level inputs and actions. AUM is an input to every "% of AUM" limit, not
// a result the optimizer changes.
export default function OptimizerRunToolbar({
    objective,
    onObjectiveChange,
    aum,
    onAumChange,
    inputsDisabled = false,
    onRun,
    showRun = true,
    runDisabled,
    runTooltip,
    onSave,
    saveDisabled,
    showNewRun = false,
    onNewRun,
    newRunDisabled = false,
    status = null,
    onStatusDismiss = null,
    design = null,
    executionDescription = null,
    expectedReturnSkill = null,
    onExpectedReturnSkillChange = null,
}) {
    return (
        <Paper
            className={cardClasses}
            elevation={0}
            sx={[{
                minHeight: 0,
                p: 1.25,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.25,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            }, analyticsPanelSx]}
        >
            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="objective-label">Objective</InputLabel>
                <Select
                    labelId="objective-label"
                    value={objective}
                    label="Objective"
                    disabled={inputsDisabled}
                    onChange={(event) => onObjectiveChange(event.target.value)}
                >
                    <MenuItem value="AlphaScore">Max Alpha-Score</MenuItem>
                    <MenuItem value={MAX_SHARPE_OBJECTIVE}>{MAX_SHARPE_LABEL}</MenuItem>
                    {META_OPTIMIZER_ENABLED && Object.entries(MANUAL_OBJECTIVES).filter(([key]) => key !== 'AlphaScore').map(([key, option]) => <MenuItem key={key} value={key}>{option.label}</MenuItem>)}
                    {Object.prototype.hasOwnProperty.call(HIDDEN_OBJECTIVE_LABELS, objective) && (
                        <MenuItem value={objective} disabled>
                            {HIDDEN_OBJECTIVE_LABELS[objective]}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
            {objective === MAX_SHARPE_OBJECTIVE && onExpectedReturnSkillChange && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <Tooltip title={SKILL_HELP}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Skill
                        </Typography>
                    </Tooltip>
                    <RuleLimitInput
                        value={expectedReturnSkill ?? null}
                        onChange={onExpectedReturnSkillChange}
                        unit="ratio"
                        disabled={inputsDisabled}
                        ariaLabel="Trade-idea skill"
                        width={80}
                    />
                </Box>
            )}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Tooltip title="Assets under management for this run. The denominator of every “% of AUM” limit.">
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        AUM
                    </Typography>
                </Tooltip>
                <RuleLimitInput
                    value={aum ?? null}
                    onChange={onAumChange}
                    unit="usd"
                    disabled={inputsDisabled}
                    ariaLabel="AUM"
                    width={170}
                />
            </Box>
            {showRun && <Tooltip title={runTooltip}>
                <span style={{ display: 'flex' }}>
                    <Button
                        variant="contained"
                        aria-label="Run optimization"
                        onClick={onRun}
                        disabled={runDisabled}
                        startIcon={<PlayArrowRoundedIcon />}
                        sx={[{ height: 36, px: 2 }, analyticsActionButtonSx]}
                    >
                        Run
                    </Button>
                </span>
            </Tooltip>}
            <Tooltip title="Save optimizer portfolio">
                <span style={{ display: 'flex' }}>
                    <Button
                        variant="outlined"
                        aria-label="Save optimizer portfolio"
                        onClick={onSave}
                        disabled={saveDisabled}
                        startIcon={<SaveRoundedIcon />}
                        sx={[{ height: 36, px: 2 }, analyticsActionButtonSx]}
                    >
                        Save
                    </Button>
                </span>
            </Tooltip>
            {showNewRun && (
                <Button
                    variant="text"
                    onClick={onNewRun}
                    disabled={newRunDisabled}
                    sx={[{ height: 36 }, analyticsActionButtonSx]}
                >
                    New run
                </Button>
            )}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                <RunStatus status={status} onDismiss={onStatusDismiss} />
            </Box>
            {(META_OPTIMIZER_ENABLED || objective === MAX_SHARPE_OBJECTIVE) && <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                {objective === MAX_SHARPE_OBJECTIVE ? MAX_SHARPE_HELP : objective === 'PortfolioDesign' && design
                    ? `Objective balance: ${GOALS[design.goal]}. Weights — conviction ${(design.construction ?? design.preferences).conviction}, goal ${(design.construction ?? design.preferences).risk}, trading ${(design.construction ?? design.preferences).turnover}, size deviation ${(design.construction ?? design.preferences).tracking}. Run preserves this balance; selecting another objective replaces these weights.`
                    : MANUAL_OBJECTIVES[objective]?.help}
                {executionDescription && ` ${executionDescription}`}
            </Typography>}
        </Paper>
    );
}

OptimizerRunToolbar.propTypes = {
    objective: PropTypes.string.isRequired,
    onObjectiveChange: PropTypes.func.isRequired,
    aum: PropTypes.number,
    onAumChange: PropTypes.func.isRequired,
    inputsDisabled: PropTypes.bool,
    onRun: PropTypes.func.isRequired,
    showRun: PropTypes.bool,
    runDisabled: PropTypes.bool.isRequired,
    runTooltip: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    saveDisabled: PropTypes.bool.isRequired,
    showNewRun: PropTypes.bool,
    onNewRun: PropTypes.func,
    newRunDisabled: PropTypes.bool,
    status: RunStatus.propTypes.status,
    onStatusDismiss: PropTypes.func,
    design: PropTypes.object,
    executionDescription: PropTypes.string,
    expectedReturnSkill: PropTypes.number,
    onExpectedReturnSkillChange: PropTypes.func,
};
